import type { EditorOverlayLayer, EditorTimelineClip } from '@/types/editor';
import { renderStartTimes } from './transitions';

export interface TimelineSnapResult {
  value: number;
  snappedTo: number | null;
}

export function snapTimelineTime(
  value: number,
  points: readonly number[],
  thresholdSeconds: number,
): TimelineSnapResult {
  let closest: number | null = null;
  let closestDistance = Math.max(0, thresholdSeconds);
  for (const point of points) {
    const distance = Math.abs(point - value);
    if (distance <= closestDistance) {
      closest = point;
      closestDistance = distance;
    }
  }
  return { value: closest ?? value, snappedTo: closest };
}

export function snapTimelineRange(
  start: number,
  end: number,
  points: readonly number[],
  thresholdSeconds: number,
): { start: number; end: number; snappedTo: number | null } {
  const startSnap = snapTimelineTime(start, points, thresholdSeconds);
  const endSnap = snapTimelineTime(end, points, thresholdSeconds);
  if (startSnap.snappedTo === null && endSnap.snappedTo === null) {
    return { start, end, snappedTo: null };
  }
  const startDistance = startSnap.snappedTo === null ? Infinity : Math.abs(startSnap.value - start);
  const endDistance = endSnap.snappedTo === null ? Infinity : Math.abs(endSnap.value - end);
  const delta = startDistance <= endDistance ? startSnap.value - start : endSnap.value - end;
  return {
    start: start + delta,
    end: end + delta,
    snappedTo: startDistance <= endDistance ? startSnap.snappedTo : endSnap.snappedTo,
  };
}

export function collectTimelineSnapPoints(
  clips: readonly EditorTimelineClip[],
  layers: readonly EditorOverlayLayer[],
  duration: number,
): number[] {
  const points = new Set<number>([0, Math.max(0, duration)]);
  const starts = renderStartTimes([...clips]);
  clips.forEach((clip, index) => {
    const start = starts[index] ?? clip.start;
    points.add(start);
    points.add(start + Math.max(0, clip.duration));
  });
  layers.forEach((layer) => {
    points.add(Math.max(0, layer.start));
    points.add(Math.max(0, layer.end));
  });
  return [...points].filter(Number.isFinite).sort((left, right) => left - right);
}
